import * as React from 'react';
import { FC, PropsWithChildren, ReactNode } from 'react';
import * as Router from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { NavLink } from 'react-bootstrap';
import { PublisherLinkOverrides } from '@yakoffice/navigation-rail';
import { YoAuthUser } from '@yakoffice/yakoffice-firebase';
import { usePublisherClaims } from './use-publisher-claims';
import { mapProjectIdStringToPublisherProjectId, mapPublisherProjectIdToProjectIdString } from './helpers';
import styles from './navigation-rail.module.sass';


export interface PublisherNavigationRailItemsPropsType {
    authUser: YoAuthUser;
    toolId: string;
    publisherLinkOverrides?: PublisherLinkOverrides;
    isDevSite?: boolean;
}

export const PublisherNavigationRailItems: FC<PropsWithChildren<PublisherNavigationRailItemsPropsType>> = (props) => {

    const routeParams = useParams<{ projectId: string }>();
    const projectId = mapProjectIdStringToPublisherProjectId(routeParams.projectId);
    const publisherClaims = usePublisherClaims(projectId);

    const CategoriesLink = () => !props.publisherLinkOverrides ? `/projects/${projectId}/versions/current/categories` : props.publisherLinkOverrides.categories;
    const DistributionsLink = () => !props.publisherLinkOverrides ? `/projects/${projectId}/versions/current/game-environments/current/distributions` : props.publisherLinkOverrides.distributions;
    const ExperimentsLink = () => !props.publisherLinkOverrides ? `/projects/${projectId}/versions/current/game-environments/current/experiments` : props.publisherLinkOverrides.experiments;
    // const CalendarLink = () => !props.publisherLinkOverrides ? `/projects/${projectId}/versions/current/game-environments/current/calendar` : props.publisherLinkOverrides.calendar;
    const SelectionsLink = () => !props.publisherLinkOverrides ? `/projects/${projectId}/versions/current/selections` : props.publisherLinkOverrides.selections;
    const GameEnvironmentsLink = () => !props.publisherLinkOverrides ? `/projects/${projectId}/game-environments` : props.publisherLinkOverrides.gameEnvironments;
    const GameVersionsLink = () => !props.publisherLinkOverrides ? `/projects/${projectId}/versions` : props.publisherLinkOverrides.gameVersions;

    const buildLink = (link: string) => {
        if (!props.publisherLinkOverrides)
            return window.location.href = `${props.isDevSite ? 'https://publisher-yakoffice-dev.web.app' : 'https://publisher.yakoffice.app'}/${link}`;
        else
            return link;
    };

    const buildNavLink = (link: string, icon: ReactNode, text: string, hiddenOnCollapsed?: boolean, end?: boolean) => {

        const styleClasses = `${styles.navigationRailNavItem} ${hiddenOnCollapsed && styles.hiddenOnCollapsed} ${hiddenOnCollapsed && 'hiddenOnCollapse'} navigationRailNavItem nav-link text-capitalize`;

        if (!props.publisherLinkOverrides)
            return <NavLink as="a" onClick={() => buildLink(link)}
                            className={styleClasses}>
                {icon}
                <span>{text}</span>
            </NavLink>;

        else
            return <Router.NavLink to={buildLink(link)} className={`${styleClasses} ${styles.publisher}`} end={end}>
                {icon}
                <span>{text}</span>
            </Router.NavLink>;
    };


    return publisherClaims.isLoaded
        && publisherClaims.result
        && props.authUser.isAuthorisedForTool(mapPublisherProjectIdToProjectIdString(routeParams.projectId), 'publisher')
        &&
        <>
            {buildNavLink(CategoriesLink(), <i className="fas fa-cogs" />, 'Configurations')}
            {buildNavLink(DistributionsLink(), <i className="fas fa-cloud-upload-alt" />, 'Distributions')}
            {buildNavLink(ExperimentsLink(), <i className="fas fa-flask" />, 'Experiments')}
            {/*{buildNavLink(CalendarLink(), <i className="fas fa-calendar-alt"/>, 'Schedule', true)}*/}

            {publisherClaims.result.editSelections &&
                buildNavLink(SelectionsLink(), <i className="fas fa-check-double" />, 'Selections', true)
            }

            {publisherClaims.result.editGameEnvironments &&
                buildNavLink(GameEnvironmentsLink(), <i className="fas fa-play-circle" />, 'Game Environments', true)
            }
            {publisherClaims.result.editGameVersions &&
                buildNavLink(GameVersionsLink(), <i className="fas fa-code-branch" />, 'Game Versions', true, true)
            }
        </>;
};

